import { useLocation } from 'react-router-dom';
import qs from 'query-string';

import useIsNativeWebView from '../../native-bridge/hooks/useIsWebView';

const NATIVE_FEATURES_STORE_KEY = 'account_card_native_features';

/**
 * Reads the "native_features" JSON from the URL, falling back to the value seen
 * earlier in the session, so the flags survive navigations that drop the query
 * string (i.e. from the template picker to the editor).
 *
 * @param {string?} search The current location search.
 * @returns {Object}
 */
const getNativeFeatures = search => {
  const { native_features: nativeFeaturesParam } = qs.parse(search ?? '');

  let nativeFeaturesJson = nativeFeaturesParam;

  try {
    if (nativeFeaturesParam) {
      window.sessionStorage.setItem(
        NATIVE_FEATURES_STORE_KEY,
        nativeFeaturesParam,
      );
    } else {
      nativeFeaturesJson = window.sessionStorage.getItem(
        NATIVE_FEATURES_STORE_KEY,
      );
    }
  } catch (e) {
    // Storage can be unavailable (i.e. private mode), the URL still applies.
  }

  try {
    return JSON.parse(nativeFeaturesJson ?? '{}') ?? {};
  } catch (e) {
    return {};
  }
};

/**
 * Determines whether native apps (iOS / Android) support certain features
 * on the AccountCard configurator so that they can take over control.
 *
 * Native app feature supports come in from a URL encoded query param "native_features" for example:
 * /cards/create/native_features=%7B"card_element_types_configuration"%3A%5B"ADDRESS"%2C"TEXT"%5D%7D
 * which translates to the following search query / JSON:
 *
 * "native_features": {
 *   "card_element_types_configuration": ["ADDRESS", "TEXT", "PHOTO"],
 *   "layout": ["EDGE_TO_EDGE"]
 * }
 *
 * The features are kept in sessionStorage the first time they're seen, so they
 * apply for the whole session even after the query param is gone.
 *
 * @returns {{nativeSupportsCardElementConfiguration: (function(CARD_ELEMENT_TYPE): boolean), nativeSupportsLayout: (function(NATIVE_APP_LAYOUT): boolean)}}
 */
const useAccountCardNativeFeatureSupport = () => {
  const location = useLocation();
  const nativeFeatures = getNativeFeatures(location?.search);
  const isNativeWebView = useIsNativeWebView();

  /**
   * Whether a CardElement can be configured by native app controls.
   * Always returns false if we're not in a webview.
   *
   * @param {CARD_ELEMENT_TYPE} cardElementType The CardElement type.
   * @returns {boolean}
   */
  const nativeSupportsCardElementConfiguration = cardElementType => {
    if (
      !nativeFeatures?.card_element_types_configuration?.length ||
      !isNativeWebView
    ) {
      return false;
    }

    return !!nativeFeatures.card_element_types_configuration?.includes(
      cardElementType,
    );
  };

  /**
   * Whether the native app renders the page with the given layout.
   * Always returns false if we're not in a webview.
   *
   * @param {NATIVE_APP_LAYOUT} layout The page layout.
   * @returns {boolean}
   */
  const nativeSupportsLayout = layout =>
    isNativeWebView && !!nativeFeatures?.layout?.includes(layout);

  return {
    nativeSupportsCardElementConfiguration,
    nativeSupportsLayout,
  };
};

export default useAccountCardNativeFeatureSupport;
